import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSites } from 'store/sitesSlice';
import { fetchProviders } from 'store/paymentProvidersSlice';
import { fetchPendingWithdrawals, initialParams } from 'store/transactionsSlice';
import { RootState } from 'store/index';
import { commonGridStyles } from 'styles/gridStyles';
import styles from './Withdraw.module.scss';
import commonStyles from 'styles/common.module.scss';
import ComponentLoader from 'components/common/ComponentLoader';
import { formatDate } from 'utils/date';

import dayjs, { Dayjs } from 'dayjs';
import RangeDatePicker from 'components/common/RangeDatePicker/RangeDatePicker';
import CustomPagination from 'components/common/pagination/pagination';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import ConfirmModal from 'components/modals/ConfirmModal';
import ManualRateWithdrawModal from 'components/modals/ManualRateWithdrawModal';
import { transactionService } from 'components/api/transactions';
import { Alert } from 'components/alert/alert';
import { showLoader, hideLoader } from 'store/uiSlice';
import { Transaction } from 'types/transaction';

const ManualWithdraw: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );
  const { data: providers, isLoading: providersLoading } = useAppSelector(
    (state: RootState) => state.paymentProviders,
  );
  const { data, total, isLoading } = useAppSelector((state) => state.transactions.pendingWithdraw);

  const [params, setParams] = useState(initialParams);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Modal state
  const [modal, setModal] = useState<{
    open: boolean;
    type: 'confirm' | 'reject' | null;
    rowId: string | number | null;
    username: string;
  }>({
    open: false,
    type: null,
    rowId: null,
    username: '',
  });

  // Rows held for pendingReason === 'rate_unavailable' need a hand-entered
  // crypto amount, so they get a dedicated modal instead of ConfirmModal.
  const [manualRateModal, setManualRateModal] = useState<{
    open: boolean;
    row: Transaction | null;
  }>({
    open: false,
    row: null,
  });

  // Alert state
  const [statusAlert, setStatusAlert] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const fromDate = params.dateFrom ? dayjs(params.dateFrom) : null;
  const toDate = params.dateTo ? dayjs(params.dateTo) : null;

  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    }
  }, [dispatch, sites.length]);

  useEffect(() => {
    if (providers.length === 0) {
      dispatch(fetchProviders());
    }
  }, [dispatch, providers.length]);

  useEffect(() => {
    if (sites.length > 0 && isInitialLoad) {
      const defaultSite = sites.find((s) => s.title.toLowerCase().includes('dgenta')) || sites[0];
      if (defaultSite) {
        setParams((prev) => ({ ...prev, siteId: defaultSite.id }));
        setIsInitialLoad(false);
      }
    }
  }, [sites, isInitialLoad]);

  // Auto-hide alert
  useEffect(() => {
    if (statusAlert) {
      const timer = setTimeout(() => {
        setStatusAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [statusAlert]);

  const loadData = useCallback(() => {
    if (!isInitialLoad && params.siteId !== 'all') {
      dispatch(fetchPendingWithdrawals(params));
    }
  }, [dispatch, params, isInitialLoad]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleActionConfirm = async () => {
    if (!modal.rowId || !modal.type) return;

    dispatch(showLoader({ text: modal.type === 'confirm' ? 'Confirming...' : 'Rejecting...' }));
    try {
      if (modal.type === 'confirm') {
        await transactionService.confirmPendingWithdrawal(modal.rowId);
        setStatusAlert({ message: 'Withdrawal confirmed successfully', type: 'success' });
      } else {
        await transactionService.rejectPendingWithdrawal(modal.rowId);
        setStatusAlert({ message: 'Withdrawal rejected successfully', type: 'success' });
      }
      loadData();
    } catch (error: any) {
      setStatusAlert({
        message: error.response?.data?.message || `Failed to ${modal.type} withdrawal`,
        type: 'error',
      });
    } finally {
      dispatch(hideLoader());
      setModal({ open: false, type: null, rowId: null, username: '' });
    }
  };

  const handleManualRateConfirm = async (receiveAmount: string) => {
    const row = manualRateModal.row;
    if (!row) return;

    dispatch(showLoader({ text: 'Confirming...' }));
    try {
      await transactionService.confirmPendingWithdrawal(row.id, receiveAmount);
      setStatusAlert({ message: 'Withdrawal confirmed successfully', type: 'success' });
      loadData();
    } catch (error: any) {
      setStatusAlert({
        message: error.response?.data?.message || 'Failed to confirm withdrawal',
        type: 'error',
      });
    } finally {
      dispatch(hideLoader());
      setManualRateModal({ open: false, row: null });
    }
  };

  const handleSiteChange = (siteId: string | number) => {
    setParams((prev) => ({ ...prev, siteId, page: 1 }));
  };

  const handleStatusChange = (status: string) => {
    setParams((prev) => ({ ...prev, status, page: 1 }));
  };

  const handleProviderChange = (providerId: string | number) => {
    setParams((prev) => ({ ...prev, providerId, page: 1 }));
  };

  const handleDateChange = (from: Dayjs | null, to: Dayjs | null) => {
    setParams((prev) => ({
      ...prev,
      dateFrom: from ? from.toISOString() : null,
      dateTo: to ? to.toISOString() : null,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setParams((prev) => ({
        ...prev,
        sortField: model[0].field,
        sortValue: model[0].sort?.toUpperCase() as 'ASC' | 'DESC',
      }));
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'user',
      headerName: 'User',
      width: 150,
      renderCell: (params) => (
        <span
          className={styles.userLink}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${params.row.user?.id || params.row.userId}`);
          }}
        >
          {params.row.user?.username || params.value || '-'}
        </span>
      ),
    },
    {
      field: 'cryptoName',
      headerName: 'Method',
      width: 150,
      renderCell: (params) => (
        <>
          {params.row?.methodImageUrl && (
            <img
              style={{ marginRight: '6px' }}
              width={20}
              src={params.row.methodImageUrl}
              alt={
                params.row.cryptoName || params.row.paymentSystem || params.row.system || 'Method'
              }
            />
          )}
          <Typography fontSize={12}>
            {params.row.cryptoName || params.row.paymentSystem || params.row.system || '-'}
          </Typography>
        </>
      ),
    },
    {
      field: 'provider',
      headerName: 'Provider name',
      width: 150,
      renderCell: (params) => params.row?.provider?.name || '-',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => (
        <Typography className={styles.amount}>
          {' '}
          $ {Number(params.value).toLocaleString()}{' '}
        </Typography>
      ),
    },
    {
      field: 'pendingReason',
      headerName: 'Reason',
      width: 140,
      renderCell: (params) => {
        const reason = params.row.pendingReason;
        if (!reason) return '-';

        if (reason === 'rate_unavailable') {
          return (
            <span className={`${styles.reasonBadge} ${styles.rateUnavailable}`}>
              Rate unavailable
            </span>
          );
        }
        if (reason === 'provider_error') {
          return (
            <span className={`${styles.reasonBadge} ${styles.providerError}`}>Provider error</span>
          );
        }
        if (reason === 'amount_threshold') {
          return (
            <span className={`${styles.reasonBadge} ${styles.amountThreshold}`}>Amount limit</span>
          );
        }

        return <span className={styles.reasonBadge}>{reason}</span>;
      },
    },
    {
      field: 'site',
      headerName: 'Site',
      width: 120,
      renderCell: (params) => params.row.site?.name || params.row.site?.title || '-',
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 180,
      renderCell: (params) => formatDate(params.value || params.row.date),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <Button
            size="small"
            variant="contained"
            sx={{
              backgroundColor: '#2a8039',
              '&:hover': { backgroundColor: '#246f30' },
              textTransform: 'none',
              fontSize: '12px',
            }}
            onClick={() =>
              params.row.pendingReason === 'rate_unavailable'
                ? setManualRateModal({ open: true, row: params.row })
                : setModal({
                    open: true,
                    type: 'confirm',
                    rowId: params.row.id,
                    username: params.row.user?.username || 'user',
                  })
            }
          >
            Confirm
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{
              backgroundColor: '#ff4d4f',
              '&:hover': { backgroundColor: '#d9363e' },
              textTransform: 'none',
              fontSize: '12px',
            }}
            onClick={() =>
              setModal({
                open: true,
                type: 'reject',
                rowId: params.row.id,
                username: params.row.user?.username || 'user',
              })
            }
          >
            Reject
          </Button>
        </Stack>
      ),
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={styles.withdrawContainer}>
      <Alert message={statusAlert?.message} type={statusAlert?.type} />

      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Manual Withdrawal
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <RangeDatePicker
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={(val) => handleDateChange(val, toDate)}
              onToChange={(val) => handleDateChange(fromDate, val)}
            />
          </Box>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Site</InputLabel>
              <Select
                value={params.siteId === 'all' ? '' : params.siteId}
                label="Site"
                onChange={(e) => handleSiteChange(e.target.value as string | number)}
                disabled={sitesLoading}
              >
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Provider</InputLabel>
              <Select
                value={params.providerId || 'all'}
                label="Provider"
                onChange={(e) => handleProviderChange(e.target.value)}
                disabled={providersLoading}
              >
                <MenuItem value="all">All Providers</MenuItem>
                {providers.map((provider) => (
                  <MenuItem key={provider.code} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data}
          columns={columns}
          rowCount={total || 0}
          loading={isLoading}
          onSortModelChange={handleSortModelChange}
          sx={commonGridStyles}
        />
        {renderPagination()}
      </Box>

      <ConfirmModal
        open={modal.open}
        title={modal.type === 'confirm' ? 'Confirm Withdrawal' : 'Reject Withdrawal'}
        description={
          modal.type === 'confirm'
            ? `Are you sure you want to confirm withdrawal for ${modal.username}?`
            : `Are you sure you want to reject withdrawal for ${modal.username}?`
        }
        confirmText={modal.type === 'confirm' ? 'Confirm' : 'Reject'}
        isDanger={modal.type === 'reject'}
        onClose={() => setModal({ ...modal, open: false })}
        onConfirm={handleActionConfirm}
      />

      <ManualRateWithdrawModal
        open={manualRateModal.open}
        username={manualRateModal.row?.user?.username || 'user'}
        amount={manualRateModal.row?.amount ?? 0}
        cryptoCode={manualRateModal.row?.cryptoCode ?? null}
        address={manualRateModal.row?.address ?? null}
        onClose={() => setManualRateModal({ open: false, row: null })}
        onConfirm={handleManualRateConfirm}
      />
    </Box>
  );
};

export default ManualWithdraw;
